import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import SelectInput from "components/forms/SelectInput";
import { getClients } from "api/Clients";
import { createEstimate } from "api/InstantEstimate";
import { convertWorkOrder } from "api/ServicePlans";
import { saveMulchMap } from "api/MulchMap";
import { formatNumber } from "../constants";
import styles from "../styles.module.scss";

interface Props {
  locationId: string | null; // null in ad-hoc mode
  /**
   * Session's mapGroupId. When the RFQ save path persists polygons
   * (dirty or ad-hoc), we pass this so the save appends to the correct
   * Map's chain instead of forking a brand-new Map at the location.
   */
  mapGroupId?: string | null;
  /**
   * Optional Map name to preserve across the RFQ-triggered save so the
   * newly saved version doesn't drop back to "Untitled map".
   */
  mapName?: string | null;
  address: {
    line1: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  } | null;
  latitude: number | null;
  longitude: number | null;
  productId: string;
  totalCuYd: number;
  totalSqFt: number;
  // Snapshot of the current zones — used to persist the mulch map after the
  // Estimate call returns a location.id (ad-hoc mode).
  zonesForSave: Array<{
    name: string;
    color: string;
    depthInches: number;
    areaSqMeters: number;
    path: Array<{ lat: number; lng: number }>;
    displayOffsets?: Record<string, { dx: number; dy: number }>;
    sortOrder: number;
  }>;
  basemap?: string;
  mapCenter: { lat: number; lng: number } | null;
  mapZoom: number | null;
  dirty: boolean;
  onClose: () => void;
}

const CreateRfqModal: React.FC<Props> = ({
  locationId,
  mapGroupId,
  mapName,
  address,
  latitude,
  longitude,
  productId,
  totalCuYd,
  totalSqFt,
  zonesForSave,
  basemap,
  mapCenter,
  mapZoom,
  dirty,
  onClose,
}) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Open work order(s) for this client at this site (409 OPEN_WORK_ORDER):
  // price the work order (RFQ created linked to it, by id) or say this is
  // separate work. 2026-10-15.
  const [woMatches, setWoMatches] = useState<any[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingClients(true);
    getClients({ query: { recordsPer: 500 } }).then((res: any) => {
      if (cancelled) return;
      const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      setClients(items);
      setLoadingClients(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const clientOptions = useMemo(
    () =>
      clients.map((c: any) => ({
        value: c.id,
        label: c.name || c.companyName || c.primaryUser?.email || c.id,
      })),
    [clients],
  );

  const canSubmit =
    !!selectedClientId &&
    !!productId &&
    !!address?.line1 &&
    latitude != null &&
    longitude != null &&
    totalCuYd > 0 &&
    !submitting;

  // Persist the drawn map against a location so the polygons survive the redirect.
  const saveMapAt = async (targetLocationId?: string) => {
    if (targetLocationId && (dirty || !locationId) && zonesForSave.length > 0) {
      await saveMulchMap({
        locationId: targetLocationId,
        mapGroupId: mapGroupId ?? undefined,
        name: mapName ?? undefined,
        mapCenterLat: mapCenter?.lat,
        mapCenterLng: mapCenter?.lng,
        mapZoom: mapZoom ?? undefined,
        basemap,
        zones: zonesForSave,
      });
    }
  };

  const priceWorkOrder = async (m: any) => {
    setSubmitting(true);
    setError(null);
    const quantity = Math.max(1, Math.round(totalCuYd));
    const res: any = await convertWorkOrder({ data: { id: m.id }, overrides: { quantity, ...(productId ? { productId } : {}) } });
    if (res?.rfqId || res?.error === "Already converted") {
      await saveMapAt(m.locationId);
      navigate(`/maintenance/work-orders/${m.id}`);
      return;
    }
    setError(res?.message || "Could not generate the RFQ for this work order.");
    setSubmitting(false);
  };

  const handleCreate = async (separate = false) => {
    if (!canSubmit && !separate) return;
    setSubmitting(true);
    setError(null);

    try {
      // Round yardage to integer for the estimate quantity — the pricing
      // engine expects a whole-number quantity in the selected unit.
      const quantity = Math.max(1, Math.round(totalCuYd));

      const est: any = await createEstimate({
        data: {
          clientId: selectedClientId,
          address,
          latitude,
          longitude,
          quantity,
          unit: "yds",
          productId,
          ...(separate ? { workOrderAction: "separate" } : {}),
        },
      });

      if (est?.statusCode === 409 && est?.code === "OPEN_WORK_ORDER" && Array.isArray(est?.matches) && est.matches.length) {
        setWoMatches(est.matches);
        setSubmitting(false);
        return;
      }

      if (!est?.id) {
        setError(est?.message || est?.error || "Failed to create RFQ.");
        setSubmitting(false);
        return;
      }

      const newLocationId: string | undefined = est.location?.id || undefined;
      const rfqId: string | undefined = est.quoteRequest?.id;

      // Persist the mulch map against the new (or existing) location. The
      // session's mapGroupId keeps the save on the correct Map's chain.
      await saveMapAt(newLocationId);

      if (rfqId) {
        navigate(`/quote-requests/${rfqId}`);
      } else {
        // Fallback: land on the estimate detail if RFQ id didn't come back
        navigate(`/quote-requests`);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to create RFQ.");
      setSubmitting(false);
    }
  };

  const missing = [
    !productId && "default product",
    !address?.line1 && "site address",
    latitude == null && "coordinates",
    totalCuYd === 0 && "at least one zone",
  ].filter(Boolean) as string[];

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>Create RFQ from site map</ModalHeader>
      <ModalBody>
        <div className={styles.rfqSummary}>
          <div className={styles.rfqSummaryRow}>
            <span>Site</span>
            <strong>
              {address?.line1 || "—"}
              {address?.city ? `, ${address.city}` : ""}
              {address?.state ? `, ${address.state}` : ""}
            </strong>
          </div>
          <div className={styles.rfqSummaryRow}>
            <span>Yardage</span>
            <strong>
              {formatNumber(totalCuYd, 1)} cu yd
              <span className={styles.rfqSummarySub}>
                {" "}
                · {formatNumber(totalSqFt)} sq ft
              </span>
            </strong>
          </div>
        </div>

        {missing.length > 0 && (
          <p className={styles.rfqWarning}>
            Missing: {missing.join(", ")}. Fix these before creating the RFQ.
          </p>
        )}

        <div style={{ marginTop: 16 }}>
          <SelectInput
            label="Client"
            name="clientId"
            options={clientOptions}
            value={selectedClientId}
            searchable
            clearable
            required
            disabled={loadingClients}
            onChange={(v: any) => {
              setSelectedClientId(v ? String(v) : "");
            }}
          />
        </div>

        {woMatches && (
          <div style={{ marginTop: 12, padding: "10px 12px", border: "1px solid #E7CC7A", background: "#FFF8E1", borderRadius: 6, fontFamily: "Poppins", fontSize: 13 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {woMatches.length > 1 ? `${woMatches.length} open work orders at this site` : "Open work order at this site"}
            </div>
            <div style={{ fontSize: 12, color: "#5f5e5a", marginBottom: 8 }}>
              Price the work order so the RFQ is attached to it. The map is saved to the work order&apos;s site.
            </div>
            {woMatches.map((m: any) => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: "1px solid #F0E3B5" }}>
                <span>{[m.externalId ? `WO ${m.externalId}` : "Work order", m.siteName].filter(Boolean).join(" \u00b7 ")}</span>
                <BasicButton type="button" keepText onClick={() => priceWorkOrder(m)} disabled={submitting}>
                  Price work order
                </BasicButton>
              </div>
            ))}
            <div style={{ marginTop: 8 }}>
              <BasicButton type="button" outline scheme="secondary" keepText disabled={submitting} onClick={() => { setWoMatches(null); handleCreate(true); }}>
                Not for a work order - create the RFQ
              </BasicButton>
            </div>
          </div>
        )}

        {error && <p className={styles.rfqWarning}>{error}</p>}
      </ModalBody>
      <ModalFooter>
        <BasicButton
          type="button"
          outline
          scheme="secondary"
          onClick={onClose}
          styles={{ marginRight: "10px" }}
        >
          Cancel
        </BasicButton>
        <BasicButton
          type="button"
          onClick={() => handleCreate()}
          $submitting={submitting}
          disabled={!canSubmit || !!woMatches}
        >
          {submitting ? "Creating…" : "Create RFQ"}
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default CreateRfqModal;
